import { useTranslation } from 'react-i18next';
import { Button, Modal } from '@teo/components';
import { useState, useEffect } from 'react';
import { newUid } from '@/util';
import { Portal } from '@/components/Portal';

const VIDEO_CONSTRAINTS_BASE = {
  audio: false,
  video: {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { ideal: 30, max: 60 },
    facingMode: { ideal: 'environment' },
  },
};

export const CameraModal = ({
  setIsOpenPhotoModal,
  isOpenPhotoModal,
  setUploadFile,
}) => {
  const [showWebcamPreview, setShowWebcamPreview] = useState(false);
  const [mediaStream, setMediaStream] = useState();
  const [uid] = useState(newUid(20));
  const [videoInputs, setVideoInputs] = useState([]);
  const [cameraIndex, setCameraIndex] = useState(0);
  const [canSwitch, setCanSwitch] = useState(false);
  const { t } = useTranslation();

  const getVideoEl = () => document.getElementById(uid);
  const getCanvasEl = () => document.getElementById(uid + '_canvas');

  const stop = () => {
    if (!mediaStream) return;
    mediaStream.getTracks().forEach((track) => track.stop());
    setMediaStream(undefined);
  };

  const enumerateVideoInputs = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const vids = devices.filter((d) => d.kind === 'videoinput');
      setVideoInputs(vids);
      setCanSwitch(vids.length > 1);
      setCameraIndex((idx) => Math.min(idx, Math.max(0, vids.length - 1)));
    } catch (e) {
      console.error('enumerateDevices failed', e);
      setVideoInputs([]);
      setCanSwitch(false);
    }
  };

  const buildConstraintsForIndex = (idx) => {
    const base = JSON.parse(JSON.stringify(VIDEO_CONSTRAINTS_BASE));
    const dev = videoInputs[idx];
    if (dev && dev.deviceId) {
      base.video.deviceId = { exact: dev.deviceId };
      delete base.video.facingMode; // avoid conflicts
    }
    return base;
  };

  const start = async (idx = 0) => {
    try {
      const constraints =
        videoInputs.length > 0
          ? buildConstraintsForIndex(idx)
          : VIDEO_CONSTRAINTS_BASE;

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      stop(); // close any previous stream
      setMediaStream(stream);

      const video = getVideoEl();
      if (video) {
        video.srcObject = stream;
        await video.play().catch(() => {});
      }

      // After permission, device labels become available
      await enumerateVideoInputs();
    } catch (err) {
      console.error(err);
    }
  };

  // Autostart/cleanup when modal opens/closes
  useEffect(() => {
    if (isOpenPhotoModal && !mediaStream) {
      start(cameraIndex);
    }
    if (!isOpenPhotoModal) {
      stop();
      setShowWebcamPreview(false);
    }
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenPhotoModal]);

  const takePicture = () => {
    const video = getVideoEl();
    const canvas = getCanvasEl();
    if (!video || !canvas) return;
    canvas.width = video.videoWidth || 1920;
    canvas.height = video.videoHeight || 1080;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.drawImage(video, 0, 0);
    setShowWebcamPreview(true);
  };

  const resetCamera = () => {
    setShowWebcamPreview(false);
    stop();
    start(cameraIndex);
  };

  const insertPicture = () => {
    const canvas = getCanvasEl();
    if (!canvas) return;
    canvas.toBlob(
      (blob) => {
        stop();
        setShowWebcamPreview(false);
        if (blob) setUploadFile((prev) => [...prev, blob]);
        setIsOpenPhotoModal(!isOpenPhotoModal);
      },
      'image/jpeg',
      0.92
    );
  };

  const closeModal = () => {
    stop();
    setIsOpenPhotoModal(!isOpenPhotoModal);
  };

  const switchCamera = async () => {
    if (!canSwitch || videoInputs.length === 0) return;
    const nextIndex = (cameraIndex + 1) % videoInputs.length;
    setCameraIndex(nextIndex);
    setShowWebcamPreview(false);
    stop();
    await start(nextIndex);
  };

  return (
    <Portal root="body">
      <div className="z-10 bg-white">
        <Modal isOpen={isOpenPhotoModal}>
          <Modal.Body>
            <div className="max-h-[70vh] overflow-hidden">
              {!showWebcamPreview && (
                <video
                  autoPlay
                  style={{
                    minWidth: '100%',
                    minHeight: '100%',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'cover',
                  }}
                  id={uid}
                ></video>
              )}
              <canvas
                style={{
                  minWidth: '100%',
                  minHeight: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'cover',
                  display: showWebcamPreview ? 'initial' : 'none',
                }}
                id={uid + '_canvas'}
              ></canvas>
            </div>
          </Modal.Body>

          <Modal.Footer
            cancelButtonLabel={t('widgets.upload_buttons.cancel_button')}
            onCancel={closeModal}
          >
            {!showWebcamPreview ? (
              <div className="flex gap-3">
                {canSwitch && (
                  <Button type="button" onClick={switchCamera}>
                    {t('widgets.upload_buttons.switch_camera') ||
                      'Switch camera'}
                  </Button>
                )}
                <Button type="button" onClick={takePicture}>
                  {t('widgets.upload_buttons.take_picture')}
                </Button>
              </div>
            ) : (
              <div className="flex gap-3">
                <Button type="button" onClick={resetCamera}>
                  {t('widgets.upload_buttons.try_again')}
                </Button>
                <Button type="button" onClick={insertPicture}>
                  {t('widgets.upload_buttons.insert')}
                </Button>
              </div>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    </Portal>
  );
};
